"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.comparisonWithEquals = comparisonWithEquals;
exports.isSignOperator = isSignOperator;
exports.operatorForCharacter = operatorForCharacter;
exports.isNumberDraft = isNumberDraft;
exports.resolveWord = resolveWord;
exports.getSuggestions = getSuggestions;
exports.ingestExpressionText = ingestExpressionText;
exports.expressionToText = expressionToText;
exports.validateExpression = validateExpression;
const OPERATOR_DEFINITIONS = [
    { symbol: '×', name: 'multiplication', aliases: ['×', 'x', '*'] },
    { symbol: '/', name: 'division', aliases: ['/', '÷'] },
    { symbol: '+', name: 'addition', aliases: ['+'] },
    { symbol: '−', name: 'subtraction', aliases: ['-', '−'] },
    { symbol: '≤', name: 'at most', aliases: ['≤'] },
    { symbol: '≥', name: 'at least', aliases: ['≥'] },
    { symbol: '<', name: 'less than', aliases: ['<'] },
    { symbol: '>', name: 'greater than', aliases: ['>'] },
    { symbol: '(', name: 'open parenthesis', aliases: ['('] },
    { symbol: ')', name: 'close parenthesis', aliases: [')'] },
];
const UNIT_DEFINITIONS = [
    { symbol: 'V', name: 'volts' },
    { symbol: 'mV', name: 'millivolts' },
    { symbol: 'µV', name: 'microvolts' },
    { symbol: 'A', name: 'amps' },
    { symbol: 'mA', name: 'milliamps' },
    { symbol: 'µA', name: 'microamps' },
    { symbol: 'nA', name: 'nanoamps' },
    { symbol: 'W', name: 'watts' },
    { symbol: 'mW', name: 'milliwatts' },
    { symbol: 'µW', name: 'microwatts' },
    { symbol: 'Ω', name: 'ohms' },
    { symbol: 'kΩ', name: 'kilohms' },
    { symbol: 'MΩ', name: 'megohms' },
    { symbol: 'Hz', name: 'hertz' },
    { symbol: 'kHz', name: 'kilohertz' },
    { symbol: 'MHz', name: 'megahertz' },
    { symbol: 's', name: 'seconds' },
    { symbol: 'ms', name: 'milliseconds' },
    { symbol: 'µs', name: 'microseconds' },
    { symbol: 'ns', name: 'nanoseconds' },
    { symbol: '°C', name: 'degrees Celsius' },
    { symbol: 'pF', name: 'picofarads' },
    { symbol: 'nF', name: 'nanofarads' },
    { symbol: 'µF', name: 'microfarads' },
];
const FUNCTION_DEFINITIONS = ['Min', 'Max', 'Abs', 'Round', 'Floor', 'Ceil'];
const SEPARATOR_CHARACTER = ',';
const NUMBER_PATTERN = /^(?:\d+(?:\.\d*)?|\.\d+)$/;
const DRAFT_CHARACTER = /[\p{L}\p{N}_.°µΩ]/u;
function makeOperatorToken(definition) {
    return { kind: 'operator', symbol: definition.symbol, name: definition.name };
}
function makeUnitToken(definition) {
    return { kind: 'unit', symbol: definition.symbol, name: definition.name };
}
function isParen(token, side) {
    return token?.kind === 'operator' && token.symbol === side;
}
const COMPARISON_SYMBOLS = ['<', '>', '≤', '≥'];
const COMPARISON_ALIASES = [
    ['<=', '≤'],
    ['>=', '≥'],
];
function comparisonWithEquals(token) {
    if (token?.kind !== 'operator') {
        return null;
    }
    const upgraded = token.symbol === '<' ? '≤'
        : token.symbol === '>' ? '≥'
            : null;
    return upgraded === null ? null : operatorForCharacter(upgraded);
}
function isComparison(token) {
    return token?.kind === 'operator' && COMPARISON_SYMBOLS.includes(token.symbol);
}
function isSignOperator(tokens, index) {
    const token = tokens[index];
    if (token?.kind !== 'operator' || (token.symbol !== '+' && token.symbol !== '−')) {
        return false;
    }
    if (tokens[index + 1]?.kind !== 'number') {
        return false;
    }
    const previous = tokens[index - 1];
    return (previous === undefined || previous.kind === 'separator' || (previous.kind === 'operator' && !isParen(previous, ')')));
}
function operatorForCharacter(character) {
    const definition = OPERATOR_DEFINITIONS.find((candidate) => candidate.aliases.includes(character.toLowerCase()));
    return definition === undefined ? null : makeOperatorToken(definition);
}
function isNumberDraft(draft) {
    return NUMBER_PATTERN.test(draft);
}
function resolveWord(word, signals) {
    if (isNumberDraft(word) && !word.endsWith('.')) {
        return { kind: 'number', text: word };
    }
    if (word.length === 1) {
        const operator = operatorForCharacter(word);
        if (operator !== null) {
            return operator;
        }
    }
    const fn = FUNCTION_DEFINITIONS.find((name) => name.toLowerCase() === word.toLowerCase());
    if (fn !== undefined) {
        return { kind: 'function', name: fn };
    }
    const signal = signals.find((candidate) => candidate.name.toLowerCase() === word.toLowerCase());
    if (signal !== undefined) {
        return { kind: 'signal', signal };
    }
    const unit = UNIT_DEFINITIONS.find((candidate) => candidate.symbol.toLowerCase() === word.toLowerCase());
    if (unit !== undefined) {
        return makeUnitToken(unit);
    }
    return null;
}
function getSuggestions(draft, signals, getSignalContext) {
    const query = draft.trim();
    if (query === '' || isNumberDraft(query)) {
        return [];
    }
    const lower = query.toLowerCase();
    const suggestions = [];
    if (query.length === 1) {
        const operator = operatorForCharacter(query);
        if (operator !== null) {
            suggestions.push({ display: query, context: operator.name, token: operator });
        }
    }
    const functions = FUNCTION_DEFINITIONS.filter((name) => name.toLowerCase().startsWith(lower)).map((name) => ({ display: name, context: 'function', token: { kind: 'function', name } }));
    const units = UNIT_DEFINITIONS.filter((unit) => unit.symbol.toLowerCase().startsWith(lower)).map((unit) => ({ display: unit.symbol, context: 'units', token: makeUnitToken(unit) }));
    const matchedSignals = signals
        .filter((signal) => signal.name.toLowerCase().startsWith(lower))
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }))
        .map((signal) => ({
        display: signal.name,
        context: getSignalContext(signal),
        token: { kind: 'signal', signal },
    }));
    suggestions.push(...functions, ...units, ...matchedSignals);
    const exactRank = (suggestion) => {
        if (suggestion.display.toLowerCase() !== lower) {
            return 0;
        }
        return (suggestion.token.kind === 'operator' ? 4
            : suggestion.token.kind === 'function' ? 3
                : suggestion.token.kind === 'signal' ? 2
                    : 1);
    };
    return suggestions.sort((a, b) => exactRank(b) - exactRank(a));
}
function ingestExpressionText(text, signals) {
    const tokens = [];
    let draft = '';
    let error = null;
    let folded = text;
    for (const [alias, symbol] of COMPARISON_ALIASES) {
        folded = folded.split(alias).join(symbol);
    }
    const commitDraft = () => {
        if (draft === '') {
            return true;
        }
        const token = resolveWord(draft, signals);
        if (token === null) {
            error = `"${draft}" isn't a known signal, function, unit, or operator.`;
            return false;
        }
        tokens.push(token);
        draft = '';
        return true;
    };
    for (const character of folded) {
        if (error !== null) {
            break;
        }
        if (DRAFT_CHARACTER.test(character)) {
            draft += character;
            const split = /^(\d+(?:\.\d+)?|\.\d+)([\p{L}°].*)$/u.exec(draft);
            const numberPart = split?.[1];
            const rest = split?.[2];
            const prefixesSignal = signals.some((signal) => signal.name.toLowerCase().startsWith(draft.toLowerCase()));
            if (numberPart !== undefined && rest !== undefined && !prefixesSignal) {
                tokens.push({ kind: 'number', text: numberPart });
                draft = rest;
            }
            continue;
        }
        if (character === ' ') {
            commitDraft();
            continue;
        }
        if (character === SEPARATOR_CHARACTER) {
            if (commitDraft()) {
                tokens.push({ kind: 'separator' });
            }
            continue;
        }
        const operator = operatorForCharacter(character);
        if (operator !== null) {
            if (commitDraft()) {
                tokens.push(operator);
            }
            continue;
        }
        error = `"${character}" isn't a supported operator.`;
    }
    return { tokens, draft, error };
}
function tokenText(token) {
    switch (token.kind) {
        case 'signal': {
            return token.signal.name;
        }
        case 'number': {
            return token.text;
        }
        case 'function': {
            return token.name;
        }
        case 'separator': {
            return SEPARATOR_CHARACTER;
        }
        default: {
            return token.symbol;
        }
    }
}
function expressionToText(tokens) {
    let text = '';
    let previous = null;
    for (const [index, token] of tokens.entries()) {
        const noSpaceBefore = isParen(token, ')') || token.kind === 'separator' || (token.kind === 'unit' && previous?.kind === 'number');
        const noSpaceAfterPrevious = previous !== null &&
            (isParen(previous, '(') || previous.kind === 'function' || isSignOperator(tokens, index - 1));
        text += text === '' || noSpaceBefore || noSpaceAfterPrevious ? tokenText(token) : ` ${tokenText(token)}`;
        previous = token;
    }
    return text;
}
function validateExpression(tokens) {
    const parenIsFunctionCall = [];
    let expectOperand = true;
    let comparisons = 0;
    for (const [index, token] of tokens.entries()) {
        const next = tokens[index + 1];
        const previous = tokens[index - 1];
        if (token.kind === 'function' && !isParen(next, '(')) {
            return `"${token.name}" must be followed by "(".`;
        }
        const openParen = isParen(token, '(');
        const closeParen = isParen(token, ')');
        const binaryOperator = token.kind === 'operator' && !openParen && !closeParen;
        if (token.kind === 'unit') {
            if (previous?.kind !== 'number') {
                return `"${token.symbol}" must follow a number.`;
            }
        }
        else if (binaryOperator) {
            if (isComparison(token)) {
                comparisons += 1;
                if (comparisons > 1) {
                    return 'A value can state only one comparison.';
                }
                if (parenIsFunctionCall.length > 0) {
                    return `"${token.symbol}" compares the whole value, so it can't sit inside parentheses.`;
                }
            }
            if (expectOperand) {
                if (isSignOperator(tokens, index)) {
                }
                else if (isComparison(token) && previous === undefined) {
                }
                else {
                    return previous === undefined ?
                        `An expression can't start with "${token.symbol}".`
                        : `"${token.symbol}" needs a value before it.`;
                }
            }
            else {
                expectOperand = true;
            }
        }
        else if (closeParen) {
            if (expectOperand) {
                return 'A value is missing before ")".';
            }
        }
        else if (token.kind === 'separator') {
            if (expectOperand) {
                return 'A comma needs an argument before it.';
            }
            expectOperand = true;
        }
        else {
            if (!expectOperand) {
                return `"${tokenText(token)}" needs an operator before it.`;
            }
            expectOperand = openParen || token.kind === 'function';
        }
        if (openParen) {
            parenIsFunctionCall.push(previous?.kind === 'function');
            if (isParen(next, ')')) {
                return "Parentheses can't be empty.";
            }
        }
        if (closeParen) {
            if (parenIsFunctionCall.length === 0) {
                return 'Unmatched ")".';
            }
            parenIsFunctionCall.pop();
            expectOperand = false;
        }
        if (token.kind === 'separator') {
            if (parenIsFunctionCall[parenIsFunctionCall.length - 1] !== true) {
                return "A comma can only separate arguments inside a function's parentheses.";
            }
            if (previous === undefined || previous.kind === 'separator' || isParen(previous, '(')) {
                return 'A comma needs an argument before it.';
            }
            if (next === undefined || next.kind === 'separator' || isParen(next, ')')) {
                return 'A comma needs an argument after it.';
            }
        }
    }
    if (parenIsFunctionCall.length > 0) {
        return 'Unclosed "(".';
    }
    const last = tokens[tokens.length - 1];
    if (expectOperand && last !== undefined) {
        return last.kind === 'operator' ? `"${last.symbol}" needs a value after it.` : 'The expression is incomplete.';
    }
    return null;
}
//# sourceMappingURL=expression-tokens.js.map